import { 
  collection, 
  addDoc, 
  doc, 
  getDocs, 
  query, 
  where,
  orderBy, 
  onSnapshot, 
  limit,
  serverTimestamp,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import { Timestamp } from 'firebase/firestore';
import { normalizeFirebaseStorageUrl } from './storageUrls';

const normalizeChatRoom = (room: ChatRoom): ChatRoom => ({
  ...room,
  bannerImageUrl: normalizeFirebaseStorageUrl(room.bannerImageUrl),
  logoImageUrl: normalizeFirebaseStorageUrl(room.logoImageUrl),
  createdByImageUrl: normalizeFirebaseStorageUrl(room.createdByImageUrl)
});

const normalizeChatRoomMessage = (message: ChatRoomMessage): ChatRoomMessage => ({
  ...message,
  senderImageUrl: normalizeFirebaseStorageUrl(message.senderImageUrl)
});

// Chat Room Message Types
export interface ChatRoomMessage {
  id: string;
  roomId: string;
  senderId: string;
  senderName: string;
  senderHandle?: string;
  senderImageUrl?: string;
  senderRole?: 'user' | 'admin' | 'moderator' | 'developer';
  content: string;
  timestamp: Date | Timestamp;
  messageType: 'text' | 'system';
}

export interface ChatRoom {
  id: string;
  name: string;
  description?: string;
  bannerImageUrl?: string;
  logoImageUrl?: string;
  createdBy: string;
  createdByName: string;
  createdByHandle?: string;
  createdByImageUrl?: string;
  createdAt: Date | Timestamp;
  memberCount?: number;
  isActive: boolean;
  category?: string;
  tags?: string[];
}

// Send a message to the community chat room
export const sendChatRoomMessage = async (
  roomId: string,
  senderId: string,
  senderName: string,
  senderHandle: string | undefined,
  senderImageUrl: string | undefined,
  senderRole: 'user' | 'admin' | 'moderator' | 'developer' | undefined,
  content: string
): Promise<string> => {
  try {
    const messageData = {
      roomId,
      senderId,
      senderName,
      senderHandle: senderHandle || null,
      senderImageUrl: senderImageUrl || null,
      senderRole: senderRole || 'user',
      content: content.trim(),
      timestamp: serverTimestamp(),
      messageType: 'text' as const
    };

    const docRef = await addDoc(collection(db, 'chatRooms', roomId, 'messages'), messageData);
    return docRef.id;
  } catch (error) {
    console.error('Error sending chat room message:', error);
    throw error;
  }
};

// Subscribe to chat room messages in real-time
export const subscribeToChatRoomMessages = (
  roomId: string,
  callback: (messages: ChatRoomMessage[]) => void,
  messageLimit: number = 50
): () => void => {
  const q = query(
    collection(db, 'chatRooms', roomId, 'messages'),
    orderBy('timestamp', 'desc'),
    limit(messageLimit)
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(doc => {
      const data = doc.data();
      return normalizeChatRoomMessage({
        id: doc.id,
        ...data,
        timestamp: data.timestamp || new Date()
      } as ChatRoomMessage);
    });
    // Reverse to show oldest first
    callback(messages.reverse());
  });
};

// Get chat room messages (non-real-time)
export const getChatRoomMessages = async (
  roomId: string,
  limitCount: number = 50
): Promise<ChatRoomMessage[]> => {
  try {
    const q = query(
      collection(db, 'chatRooms', roomId, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const messages = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return normalizeChatRoomMessage({
        id: doc.id,
        ...data,
        timestamp: data.timestamp || new Date()
      } as ChatRoomMessage);
    });
    
    return messages.reverse(); // Reverse to show oldest first
  } catch (error) {
    console.error('Error fetching chat room messages:', error);
    return [];
  }
};

// Create or get the main community chat room
export const getOrCreateCommunityChatRoom = async (): Promise<string> => {
  try {
    // Use a fixed ID for the main community chat room
    const COMMUNITY_CHAT_ID = 'community-main';
    const roomRef = doc(db, 'chatRooms', COMMUNITY_CHAT_ID);
    
    // Check if room already exists
    const roomSnap = await getDoc(roomRef);
    
    if (roomSnap.exists()) {
      return COMMUNITY_CHAT_ID;
    }
    
    // Create new community chat room with fixed ID
    const roomData = {
      name: 'Community Chat',
      description: 'The main community chat room for all members to connect and share',
      createdBy: 'system',
      createdByName: 'System',
      createdAt: serverTimestamp(),
      isActive: true
    };
    
    // Use setDoc to create the document
    await setDoc(roomRef, roomData);
    
    return COMMUNITY_CHAT_ID;
  } catch (error) {
    console.error('Error getting/creating community chat room:', error);
    throw error;
  }
};

// Get all chat rooms
export const getChatRooms = async (): Promise<ChatRoom[]> => {
  try {
    const q = query(
      collection(db, 'chatRooms'),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc =>
      normalizeChatRoom({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || new Date()
      } as ChatRoom)
    );
  } catch (error) {
    console.error('Error fetching chat rooms:', error);
    return [];
  }
};

// Upload banner image for a topic
export const uploadTopicBanner = async (file: File, topicId: string): Promise<string> => {
  try {
    const storageRef = ref(storage, `topic-banners/${topicId}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return normalizeFirebaseStorageUrl(downloadURL) || downloadURL;
  } catch (error) {
    console.error('Error uploading topic banner:', error);
    throw error;
  }
};

// Upload logo image for a topic
export const uploadTopicLogo = async (file: File, topicId: string): Promise<string> => {
  try {
    const storageRef = ref(storage, `topic-logos/${topicId}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return normalizeFirebaseStorageUrl(downloadURL) || downloadURL;
  } catch (error) {
    console.error('Error uploading topic logo:', error);
    throw error;
  }
};

// Create a new topic (chat room)
export const createTopic = async (
  name: string,
  description: string,
  createdBy: string,
  createdByName: string,
  createdByHandle?: string,
  createdByImageUrl?: string,
  bannerImageFile?: File,
  logoImageFile?: File,
  category?: string,
  tags?: string[]
): Promise<string> => {
  try {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedCategory = category?.trim();
    const cleanedTags = (tags ?? []).map(tag => tag.trim()).filter(Boolean);

    const topicData: Omit<ChatRoom, 'id'> = {
      name: trimmedName,
      createdBy,
      createdByName,
      createdAt: serverTimestamp() as any,
      isActive: true,
      memberCount: 0,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
      ...(createdByHandle ? { createdByHandle } : {}),
      ...(createdByImageUrl ? { createdByImageUrl } : {}),
      ...(trimmedCategory ? { category: trimmedCategory } : {}),
      ...(cleanedTags.length > 0 ? { tags: cleanedTags } : {})
    };

    // Create the topic document
    const docRef = await addDoc(collection(db, 'chatRooms'), topicData);

    // Upload banner image if provided
    if (bannerImageFile) {
      try {
        const bannerUrl = await uploadTopicBanner(bannerImageFile, docRef.id);
        await setDoc(doc(db, 'chatRooms', docRef.id), {
          bannerImageUrl: bannerUrl
        }, { merge: true });
      } catch (bannerError) {
        console.error('Error uploading banner, continuing without banner:', bannerError);
      }
    }

    // Upload logo image if provided
    if (logoImageFile) {
      try {
        const logoUrl = await uploadTopicLogo(logoImageFile, docRef.id);
        await setDoc(doc(db, 'chatRooms', docRef.id), {
          logoImageUrl: logoUrl
        }, { merge: true });
      } catch (logoError) {
        console.error('Error uploading logo, continuing without logo:', logoError);
      }
    }

    return docRef.id;
  } catch (error) {
    console.error('Error creating topic:', error);
    throw error;
  }
};

// Get a specific chat room by ID
export const getChatRoomById = async (roomId: string): Promise<ChatRoom | null> => {
  try {
    const roomDoc = await getDoc(doc(db, 'chatRooms', roomId));
    if (roomDoc.exists()) {
      return normalizeChatRoom({
        id: roomDoc.id,
        ...roomDoc.data(),
        createdAt: roomDoc.data().createdAt || new Date()
      } as ChatRoom);
    }
    return null;
  } catch (error) {
    console.error('Error fetching chat room:', error);
    return null;
  }
};

// Subscribe to chat rooms list updates
export const subscribeToChatRooms = (
  callback: (rooms: ChatRoom[]) => void
): () => void => {
  const q = query(
    collection(db, 'chatRooms'),
    where('isActive', '==', true)
  );

  return onSnapshot(q, (snapshot) => {
    const rooms = snapshot.docs.map(doc =>
      normalizeChatRoom({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt || new Date()
      } as ChatRoom)
    );

    // Sort by createdAt descending
    rooms.sort((a, b) => {
      const dateA = convertChatTimestamp(a.createdAt);
      const dateB = convertChatTimestamp(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
    
    callback(rooms);
  });
};

// Helper function to convert Firestore Timestamp to Date
export const convertChatTimestamp = (timestamp: Date | Timestamp): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  return new Date();
};

// Join a chat room (add user to members)
export const joinChatRoom = async (roomId: string, userId: string): Promise<void> => {
  try {
    const roomRef = doc(db, 'chatRooms', roomId);
    const memberRef = doc(db, 'chatRooms', roomId, 'members', userId);
    
    // Check if user is already a member
    const memberSnap = await getDoc(memberRef);
    if (!memberSnap.exists()) {
      // Add user to members subcollection
      await setDoc(memberRef, {
        userId,
        joinedAt: serverTimestamp()
      });
      
      // Increment member count
      await updateDoc(roomRef, {
        memberCount: increment(1)
      });
    }
  } catch (error) {
    console.error('Error joining chat room:', error);
    throw error;
  }
};

// Leave a chat room (remove user from members)
export const leaveChatRoom = async (roomId: string, userId: string): Promise<void> => {
  try {
    const roomRef = doc(db, 'chatRooms', roomId);
    const memberRef = doc(db, 'chatRooms', roomId, 'members', userId);
    
    // Check if user is a member
    const memberSnap = await getDoc(memberRef);
    if (memberSnap.exists()) {
      // Remove user from members subcollection
      await deleteDoc(memberRef);
      
      // Decrement member count
      await updateDoc(roomRef, {
        memberCount: increment(-1)
      });
    }
  } catch (error) {
    console.error('Error leaving chat room:', error);
    throw error;
  }
};

// Check if user is a member of a chat room
export const isChatRoomMember = async (roomId: string, userId: string): Promise<boolean> => {
  try {
    const memberRef = doc(db, 'chatRooms', roomId, 'members', userId);
    const memberSnap = await getDoc(memberRef);
    return memberSnap.exists();
  } catch (error) {
    console.error('Error checking chat room membership:', error);
    return false;
  }
};

// Get all chat rooms a user has joined
export const getJoinedChatRooms = async (userId: string): Promise<ChatRoom[]> => {
  try {
    // Get all chat rooms
    const allRooms = await getChatRooms();
    
    // Filter rooms where user is a member
    const joinedRooms: ChatRoom[] = [];
    for (const room of allRooms) {
      const isMember = await isChatRoomMember(room.id, userId);
      if (isMember) {
        joinedRooms.push(room);
      }
    }
    
    return joinedRooms;
  } catch (error) {
    console.error('Error getting joined chat rooms:', error);
    return [];
  }
};

// Subscribe to joined chat rooms for a user
export const subscribeToJoinedChatRooms = (
  userId: string,
  callback: (rooms: ChatRoom[]) => void
): () => void => {
  // Subscribe to all chat rooms
  const unsubscribeRooms = subscribeToChatRooms(async (allRooms) => {
    // Filter rooms where user is a member
    const joinedRooms: ChatRoom[] = [];
    
    for (const room of allRooms) {
      try {
        const isMember = await isChatRoomMember(room.id, userId);
        if (isMember) {
          joinedRooms.push(room);
        }
      } catch (error) {
        console.error(`Error checking membership for room ${room.id}:`, error);
      }
    }
    
    callback(joinedRooms);
  });
  
  return unsubscribeRooms;
};

// Update a topic (chat room)
export const updateTopic = async (
  topicId: string,
  updates: {
    name?: string;
    description?: string;
    category?: string;
    tags?: string[];
    bannerImageFile?: File;
    logoImageFile?: File;
  }
): Promise<void> => {
  try {
    const roomRef = doc(db, 'chatRooms', topicId);
    const updateData: any = {
      ...(updates.name !== undefined && { name: updates.name.trim() }),
      ...(updates.description !== undefined && { description: updates.description.trim() || null }),
      ...(updates.category !== undefined && { category: updates.category || null }),
      ...(updates.tags !== undefined && { tags: updates.tags.length > 0 ? updates.tags : null })
    };

    // Update basic fields
    if (Object.keys(updateData).length > 0) {
      await updateDoc(roomRef, updateData);
    }

    // Upload new banner image if provided
    if (updates.bannerImageFile) {
      try {
        const bannerUrl = await uploadTopicBanner(updates.bannerImageFile, topicId);
        await updateDoc(roomRef, { bannerImageUrl: bannerUrl });
      } catch (bannerError) {
        console.error('Error uploading banner:', bannerError);
      }
    }

    // Upload new logo image if provided
    if (updates.logoImageFile) {
      try {
        const logoUrl = await uploadTopicLogo(updates.logoImageFile, topicId);
        await updateDoc(roomRef, { logoImageUrl: logoUrl });
      } catch (logoError) {
        console.error('Error uploading logo:', logoError);
      }
    }
  } catch (error) {
    console.error('Error updating topic:', error);
    throw error;
  }
};

// Delete a topic (chat room) - soft delete by setting isActive to false
export const deleteTopic = async (topicId: string): Promise<void> => {
  try {
    const roomRef = doc(db, 'chatRooms', topicId);
    // Soft delete by marking as inactive instead of hard delete
    // This preserves the chat history and membership records
    await updateDoc(roomRef, {
      isActive: false
    });
  } catch (error) {
    console.error('Error deleting topic:', error);
    throw error;
  }
};
